/**
 * HitchFire 2026 — Main JS
 * Handles: mega-menu, mobile nav, cart drawer (AJAX),
 *          hero carousel, FAQ accordion, sticky ATC,
 *          vehicle selector, press carousel, email popup,
 *          copy-code button, quantity steppers, Lucide icons.
 *
 * Load deferred in theme.liquid:
 *   <script src="{{ 'hitchfire.js' | asset_url }}" defer></script>
 */

/* ─── Init ──────────────────────────────────────────────────────── */
function hfInit() {
  // Lucide is loaded from a CDN and may be blocked/slow — never let it
  // abort the rest of the storefront JS.
  try { if (window.lucide) lucide.createIcons(); } catch (e) {}

  // Run each initializer in isolation so one failure can't kill the others.
  [
    initHfResponsive,   // map the kit's .hf-mobile class to real breakpoints
    initMegaMenu,
    initMobileNav,
    initCartDrawer,
    initHeroCarousel,
    initFaqAccordion,
    initStickyATC,
    initVehicleSelectors,
    initPressCarousel,
    initEmailPopup,
    initQtySteppers,
    initCopyCode
  ].forEach(function (fn) {
    try { fn(); } catch (e) { if (window.console) console.warn('HitchFire init error:', fn.name, e); }
  });
}

if (document.readyState !== 'loading') hfInit();
else document.addEventListener('DOMContentLoaded', hfInit);

/* ─── Responsive: toggle the kit's .hf-mobile class at the breakpoint ── */
/* The design system styles "mobile" via a .hf-mobile class on .hf-board
   (it has very few real media queries). We add/remove that class on every
   .hf-board based on viewport width so the mobile layout actually engages. */
function initHfResponsive() {
  var mq = window.matchMedia('(max-width: 768px)');
  function apply() {
    document.querySelectorAll('.hf-board').forEach(function (b) {
      b.classList.toggle('hf-mobile', mq.matches);
    });
  }
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
}

/* ─── Lucide icons (re-init after dynamic content) ─────────────── */
function reInitIcons() {
  if (window.lucide) lucide.createIcons();
}

/* ─── Mega-menu ─────────────────────────────────────────────────── */
function initMegaMenu() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const triggers = header.querySelectorAll('[data-menu-trigger]');
  const panels   = header.querySelectorAll('[data-menu-panel]');

  triggers.forEach(trigger => {
    trigger.addEventListener('mouseenter', () => {
      const id = trigger.dataset.menuTrigger;
      triggers.forEach(t => { t.classList.remove('is-open'); t.setAttribute('aria-expanded', 'false'); });
      panels.forEach(p => { p.hidden = true; });
      trigger.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      const panel = header.querySelector(`[data-menu-panel="${id}"]`);
      if (panel) panel.hidden = false;
    });
  });

  header.addEventListener('mouseleave', () => {
    triggers.forEach(t => { t.classList.remove('is-open'); t.setAttribute('aria-expanded', 'false'); });
    panels.forEach(p => { p.hidden = true; });
  });
}

/* ─── Mobile nav ────────────────────────────────────────────────── */
function initMobileNav() {
  const nav      = document.querySelector('[data-mobnav]');
  const openBtns = document.querySelectorAll('[data-nav-open]');
  const closeBtns = document.querySelectorAll('[data-nav-close]');
  if (!nav) return;

  const open  = () => { nav.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
  const close = () => { nav.classList.remove('is-open'); document.body.style.overflow = ''; };

  openBtns.forEach(b => b.addEventListener('click', open));
  closeBtns.forEach(b => b.addEventListener('click', close));

  // Accordion groups
  nav.querySelectorAll('[data-mob-group]').forEach(group => {
    const trigger = group.querySelector('[data-mob-group-trigger]');
    const body    = group.querySelector('.hf-mobnav__panel-body');
    const iconPlus  = group.querySelector('.hf-mobnav__group-icon-plus');
    const iconMinus = group.querySelector('.hf-mobnav__group-icon-minus');
    if (!trigger || !body) return;

    trigger.addEventListener('click', () => {
      const isOpen = !body.hidden;
      body.hidden = isOpen;
      group.classList.toggle('is-open', !isOpen);
      if (iconPlus)  iconPlus.hidden  = !isOpen;
      if (iconMinus) iconMinus.hidden = isOpen;
    });
  });
}

/* ─── Cart Drawer ───────────────────────────────────────────────── */
const CartDrawer = {
  el:      null,
  overlay: null,
  init() {
    this.el      = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-overlay]');

    // Delegated so it works regardless of when the cart button is rendered.
    const self = this;
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-open]'))  { e.preventDefault(); self.open(); }
      else if (e.target.closest('[data-cart-close]')) { e.preventDefault(); self.close(); }
      else if (e.target.closest('[data-cart-overlay]')) { self.close(); }
    });
  },
  open() {
    this.el = this.el || document.querySelector('[data-cart-drawer]');
    this.overlay = this.overlay || document.querySelector('[data-cart-overlay]');
    if (!this.el) return;
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  },
  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  },
  async refresh() {
    const cart = await fetch('/cart.js').then(r => r.json());
    this.render(cart);
    this.updateCount(cart.item_count);
  },
  render(cart) {
    const body  = this.el.querySelector('[data-cart-body]');
    const empty = this.el.querySelector('[data-cart-empty]');
    const title = this.el.querySelector('[data-cart-title]');
    const sub   = this.el.querySelector('[data-cart-subtotal]');
    const btn   = this.el.querySelector('[data-cart-checkout]');
    const tpl   = document.getElementById('cart-line-template');
    if (!body || !tpl) return;

    if (title) title.textContent = `Cart · ${cart.item_count}`;
    if (sub)   sub.textContent   = formatMoney(cart.total_price);
    if (btn)   btn.classList.toggle('disabled', cart.item_count === 0);
    if (empty) empty.hidden = cart.item_count > 0;

    // Clear existing lines
    body.querySelectorAll('.cart-line').forEach(l => l.remove());

    cart.items.forEach(item => {
      const clone = tpl.content.cloneNode(true);
      const line  = clone.querySelector('.cart-line');
      line.dataset.lineKey = item.key;

      const img   = clone.querySelector('.cart-line__img');
      const name  = clone.querySelector('.cart-line__title');
      const qty   = clone.querySelector('.cart-line__qty');
      const price = clone.querySelector('.cart-line__price');
      const rm    = clone.querySelector('.cart-line__remove');

      if (img)   { img.src = item.image; img.alt = item.title; }
      if (name)  name.textContent  = item.product_title;
      if (qty)   qty.textContent   = `Qty ${item.quantity} · ${item.product_type}`;
      if (price) price.textContent = formatMoney(item.line_price);
      if (rm)    rm.addEventListener('click', () => this.changeQty(item.key, 0));

      body.appendChild(clone);
    });
    reInitIcons();
  },
  async changeQty(key, qty) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    this.refresh();
  },
  updateCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }
};

function initCartDrawer() { CartDrawer.init(); }

// Add to cart (any ATC button on the page)
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-add-to-cart]');
  if (!btn) return;
  e.preventDefault();
  const id = btn.dataset.addToCart;
  if (!id) return;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: parseInt(id), quantity: 1 })
  });
  btn.disabled = false;
  btn.textContent = '✓ Added';
  setTimeout(() => { btn.textContent = '+ Add'; }, 2000);
  CartDrawer.open();
});

// ATC form submit
document.querySelectorAll('[data-atc-form], #product-atc-form').forEach(form => {
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const fd   = new FormData(form);
    const id   = fd.get('id');
    const qty  = fd.get('quantity') || 1;
    const btn  = form.querySelector('[data-atc-btn]');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(id), quantity: parseInt(qty) })
    });
    if (btn) { btn.disabled = false; btn.textContent = 'Added ✓'; setTimeout(() => location.reload(), 800); }
    CartDrawer.open();
  });
});

/* ─── Hero carousel ─────────────────────────────────────────────── */
function initHeroCarousel() {
  const hero = document.querySelector('[data-hero-carousel]');
  if (!hero) return;

  const bgs    = hero.querySelectorAll('.hero__bg');
  const copies = hero.querySelectorAll('[data-slide-copy]');
  const dots   = hero.querySelectorAll('[data-dot]');
  const delay  = (hero.dataset.autoplay || 6) * 1000;
  let current  = 0;
  let timer;

  function goTo(n) {
    bgs.forEach((b, i)    => b.classList.toggle('is-on', i === n));
    copies.forEach((c, i) => c.classList.toggle('is-on', i === n));
    dots.forEach((d, i)   => d.classList.toggle('is-on', i === n));
    current = n;
  }

  function next() { goTo((current + 1) % bgs.length); }
  function prev() { goTo((current - 1 + bgs.length) % bgs.length); }
  function autoplay() { clearTimeout(timer); timer = setTimeout(() => { next(); autoplay(); }, delay); }

  hero.querySelector('[data-hero-next]')?.addEventListener('click', () => { next(); autoplay(); });
  hero.querySelector('[data-hero-prev]')?.addEventListener('click', () => { prev(); autoplay(); });
  dots.forEach(d => d.addEventListener('click', () => { goTo(+d.dataset.dot); autoplay(); }));

  autoplay();
}

/* ─── FAQ accordion ─────────────────────────────────────────────── */
function initFaqAccordion() {
  document.querySelectorAll('[data-faq-item]').forEach(item => {
    const trigger = item.querySelector('[data-faq-trigger]');
    const answer  = item.querySelector('.faq-item__a');
    const icon    = item.querySelector('.faq-item__icon');
    if (!trigger || !answer) return;

    trigger.addEventListener('click', () => {
      const isOpen = !answer.hidden;
      answer.hidden = isOpen;
      item.classList.toggle('is-open', !isOpen);
      if (icon) icon.textContent = isOpen ? '+' : '−';
    });
  });
}

/* ─── Sticky ATC ────────────────────────────────────────────────── */
function initStickyATC() {
  const bar     = document.querySelector('[data-sticky-atc]');
  const trigger = document.querySelector('[data-product-form]');
  if (!bar || !trigger) return;

  const observer = new IntersectionObserver(([entry]) => {
    bar.classList.toggle('is-visible', !entry.isIntersecting);
    bar.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
  }, { threshold: 0 });

  observer.observe(trigger);

  // Wire sticky ATC button to the main form's submit
  bar.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
    document.querySelector('[data-atc-btn]')?.click();
  });
}

/* ─── Vehicle selector (wired to the YMM fitment dataset) ──────────── */
/* Data source: hf-fitment-data.json -> { fitment: [ {sy,ey,mk,md,...} ] }
   The asset URL is exposed by theme.liquid as window.HF_FITMENT_URL.
   On confirm we store the vehicle in the SAME localStorage keys the existing
   HitchFire fitment scripts use (ymm_values / ymm_labels) and dispatch
   'hf:vehicle', so product + collection fitment displays update. */
var HF_FIT = null, HF_FIT_PROMISE = null;
function hfLoadFitment() {
  if (HF_FIT) return Promise.resolve(HF_FIT);
  if (HF_FIT_PROMISE) return HF_FIT_PROMISE;
  var url = window.HF_FITMENT_URL;
  if (!url) return Promise.reject(new Error('HF_FITMENT_URL not set'));
  HF_FIT_PROMISE = fetch(url).then(function (r) { return r.json(); }).then(function (d) {
    var rows = (d && d.fitment) ? d.fitment : (Array.isArray(d) ? d : []);
    var yearsSet = {}, byYearMake = {};
    rows.forEach(function (r) {
      for (var y = r.sy; y <= r.ey; y++) {
        yearsSet[y] = 1;
        (byYearMake[y] = byYearMake[y] || {});
        (byYearMake[y][r.mk] = byYearMake[y][r.mk] || {});
        byYearMake[y][r.mk][r.md] = 1;
      }
    });
    HF_FIT = {
      years: Object.keys(yearsSet).map(Number).sort(function (a, b) { return b - a; }),
      byYearMake: byYearMake
    };
    return HF_FIT;
  });
  return HF_FIT_PROMISE;
}

function readVehicleYMM() {
  try {
    var raw = localStorage.getItem('ymm_values');
    if (!raw) return null;
    var p = {};
    raw.split('~').forEach(function (s) { var i = s.indexOf('_'); p[s.substring(0, i)] = s.substring(i + 1); });
    if (!p.yr || !p.mk || !p.md) return null;
    return { year: parseInt(p.yr, 10), make: p.mk, model: p.md };
  } catch (e) { return null; }
}
function saveVehicleYMM(v) {
  try {
    localStorage.setItem('ymm_values', 'yr_' + v.year + '~mk_' + v.make + '~md_' + v.model);
    localStorage.setItem('ymm_labels', v.year + '~' + v.make + '~' + v.model);
  } catch (e) {}
  document.dispatchEvent(new CustomEvent('hf:vehicle', { detail: v }));
  window.dispatchEvent(new CustomEvent('hf:vehicle-set', { detail: v }));
}
function clearVehicleYMM() {
  try { localStorage.removeItem('ymm_values'); localStorage.removeItem('ymm_labels'); } catch (e) {}
  document.dispatchEvent(new CustomEvent('hf:vehicle', { detail: null }));
}

function initVehicleSelectors() {
  var saved = readVehicleYMM();
  document.querySelectorAll('[data-veh-strip]').forEach(function (strip) { wireVehicleWidget(strip, saved); });
  var chipWrap = document.querySelector('[data-vehicle-chip-wrap]');
  if (chipWrap) initHeaderVehicleChip(chipWrap, saved);
}

function wireVehicleWidget(scope, saved) {
  var yearSel  = scope.querySelector('[data-veh-year]');
  var makeSel  = scope.querySelector('[data-veh-make]');
  var modelSel = scope.querySelector('[data-veh-model]');
  var confirmBtn = scope.querySelector('[data-veh-confirm], [data-vehicle-confirm]');
  if (!yearSel || !makeSel || !modelSel) return;

  var yearsFilled = false;
  function fillYears() {
    return hfLoadFitment().then(function (F) {
      if (!yearsFilled) {
        yearsFilled = true;
        F.years.forEach(function (y) { yearSel.appendChild(new Option(y, y)); });
        if (saved && saved.year) {
          yearSel.value = saved.year;
          fillMakes().then(function () {
            makeSel.value = saved.make;
            fillModels().then(function () { modelSel.value = saved.model; check(); });
          });
        }
      }
      return F;
    }).catch(function () {});
  }
  function fillMakes() {
    return hfLoadFitment().then(function (F) {
      makeSel.innerHTML = '<option value="">Make</option>';
      var makes = F.byYearMake[yearSel.value] ? Object.keys(F.byYearMake[yearSel.value]).sort() : [];
      makes.forEach(function (m) { makeSel.appendChild(new Option(m, m)); });
      makeSel.disabled = !yearSel.value;
      return F;
    });
  }
  function fillModels() {
    return hfLoadFitment().then(function (F) {
      modelSel.innerHTML = '<option value="">Model</option>';
      var byMake = F.byYearMake[yearSel.value] || {};
      var models = byMake[makeSel.value] ? Object.keys(byMake[makeSel.value]).sort() : [];
      models.forEach(function (m) { modelSel.appendChild(new Option(m, m)); });
      modelSel.disabled = !makeSel.value;
      return F;
    });
  }
  function check() { if (confirmBtn) confirmBtn.disabled = !(yearSel.value && makeSel.value && modelSel.value); }

  fillYears(); // loads the dataset (cached) and pre-fills any saved vehicle

  yearSel.addEventListener('change', function () {
    modelSel.innerHTML = '<option value="">Model</option>'; modelSel.disabled = true;
    fillMakes().then(check);
  });
  makeSel.addEventListener('change', function () { fillModels().then(check); });
  modelSel.addEventListener('change', check);

  if (confirmBtn) confirmBtn.addEventListener('click', function () {
    if (!(yearSel.value && makeSel.value && modelSel.value)) return;
    saveVehicleYMM({ year: parseInt(yearSel.value, 10), make: makeSel.value, model: modelSel.value });
    window.location.reload();
  });

  var clearBtn = scope.querySelector('[data-veh-clear], [data-vehicle-clear]');
  if (clearBtn) clearBtn.addEventListener('click', function () { clearVehicleYMM(); window.location.reload(); });
}

function initHeaderVehicleChip(wrap, saved) {
  var chip = wrap.querySelector('[data-vehicle-chip]');
  var pop = wrap.querySelector('[data-vehicle-pop]');
  var closeBtn = wrap.querySelector('[data-vehicle-pop-close]');
  var label = wrap.querySelector('[data-vehicle-chip-label]');
  var clearBtn = wrap.querySelector('[data-vehicle-clear]');
  if (!chip || !pop) return;

  if (saved && label) label.textContent = saved.year + ' ' + saved.make + ' ' + saved.model;
  if (saved && clearBtn) clearBtn.hidden = false;

  chip.addEventListener('click', function () {
    var isOpen = !pop.hidden;
    pop.hidden = isOpen;
    chip.setAttribute('aria-expanded', String(!isOpen));
  });
  if (closeBtn) closeBtn.addEventListener('click', function () { pop.hidden = true; chip.setAttribute('aria-expanded', 'false'); });

  wireVehicleWidget(pop, saved);
}

/* ─── Press carousel ────────────────────────────────────────────── */
function initPressCarousel() {
  document.querySelectorAll('[data-quotes-carousel]').forEach(el => {
    const pubs    = el.querySelectorAll('[data-pub]');
    const quotes  = el.querySelectorAll('[data-quote]');
    const counter = el.querySelector('[data-quotes-count]');
    const delay   = (el.dataset.autoplay || 7) * 1000;
    let current   = 0;
    let timer;

    function goTo(n) {
      pubs.forEach((p, i)   => p.classList.toggle('is-active', i === n));
      quotes.forEach((q, i) => q.classList.toggle('is-hidden', i !== n));
      if (counter) counter.textContent = `${n + 1} / ${pubs.length}`;
      current = n;
    }

    function autoplay() {
      clearTimeout(timer);
      timer = setTimeout(() => { goTo((current + 1) % pubs.length); autoplay(); }, delay);
    }

    pubs.forEach(p => p.addEventListener('click', () => { goTo(+p.dataset.pub); autoplay(); }));
    el.querySelector('[data-quotes-prev]')?.addEventListener('click', () => { goTo((current - 1 + pubs.length) % pubs.length); autoplay(); });
    el.querySelector('[data-quotes-next]')?.addEventListener('click', () => { goTo((current + 1) % pubs.length); autoplay(); });

    autoplay();
  });
}

/* ─── Email popup ───────────────────────────────────────────────── */
function initEmailPopup() {
  const modal   = document.querySelector('[data-email-modal]');
  const overlay = document.querySelector('[data-email-overlay]');
  if (!modal) return;

  const STORAGE_KEY = 'hf-popup-dismissed';
  const section = modal.closest('section') || modal;

  // Read settings from data attributes (set by Liquid section)
  const delaySec    = parseInt(modal.dataset.delay   || '8', 10);
  const suppressDays= parseInt(modal.dataset.suppress || '30', 10);
  const exitIntent  = modal.dataset.exitIntent !== 'false';

  function shouldShow() {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return true;
    const days = (Date.now() - parseInt(dismissed, 10)) / 86400000;
    return days > suppressDays;
  }

  function openPopup() {
    if (!shouldShow()) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    if (overlay) { overlay.classList.add('is-open'); }
  }

  function closePopup() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('is-open');
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }

  // Close buttons
  modal.querySelectorAll('[data-email-close]').forEach(b => b.addEventListener('click', closePopup));
  overlay?.addEventListener('click', closePopup);

  // Delay trigger
  setTimeout(openPopup, delaySec * 1000);

  // Exit intent
  if (exitIntent) {
    document.addEventListener('mouseleave', e => {
      if (e.clientY <= 0) openPopup();
    }, { once: true });
  }

  // Form submit
  const form = modal.querySelector('[data-email-form]');
  form?.addEventListener('submit', e => {
    e.preventDefault();
    const fd = new FormData(form);
    // Klaviyo identify (if Klaviyo is loaded)
    if (window._learnq) {
      window._learnq.push(['identify', {
        '$email': fd.get('email'),
        '$first_name': fd.get('first_name')
      }]);
    }
    showEmailSuccess();
  });
}

window.showEmailSuccess = function() {
  const modal   = document.querySelector('[data-email-modal]');
  if (!modal) return;
  const formPanel    = modal.querySelector('[data-email-form-panel]');
  const successPanel = modal.querySelector('[data-email-success-panel]');
  if (formPanel)    formPanel.hidden = true;
  if (successPanel) successPanel.hidden = false;
};

/* ─── Copy code ─────────────────────────────────────────────────── */
function initCopyCode() {
  document.querySelectorAll('[data-copy-code]').forEach(btn => {
    btn.addEventListener('click', () => {
      const code = btn.dataset.code || btn.closest('[data-email-modal]')
                     ?.querySelector('.email-popup__code-text')?.textContent?.trim();
      if (!code) return;
      navigator.clipboard.writeText(code).then(() => {
        btn.textContent = '✓ COPIED';
        setTimeout(() => { btn.textContent = 'COPY CODE'; }, 2200);
      });
    });
  });
}

/* ─── Qty steppers ──────────────────────────────────────────────── */
function initQtySteppers() {
  document.addEventListener('click', async e => {
    // Cart page qty change
    const changeBtn = e.target.closest('[data-change-qty]');
    if (changeBtn) {
      const key = changeBtn.dataset.changeQty;
      const qty = parseInt(changeBtn.dataset.qty, 10);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      });
      location.reload();
    }

    // Cart page remove
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.dataset.removeItem;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      location.reload();
    }

    // PDP buy-box qty
    if (e.target.closest('[data-qty-dec]')) {
      const input = e.target.closest('[data-qty]').querySelector('input');
      if (input && input.value > 1) input.value = parseInt(input.value) - 1;
    }
    if (e.target.closest('[data-qty-inc]')) {
      const input = e.target.closest('[data-qty]').querySelector('input');
      if (input) input.value = parseInt(input.value) + 1;
    }
  });
}

/* ─── Variant selector (PDP) ────────────────────────────────────── */
document.querySelectorAll('[data-option-btn]').forEach(btn => {
  btn.addEventListener('click', () => {
    const name  = btn.dataset.optionName;
    const value = btn.dataset.optionValue;
    btn.closest('.pdp-option__values')
       .querySelectorAll('[data-option-btn]')
       .forEach(b => b.classList.toggle('is-selected', b === btn));

    // Find matching variant and update hidden inputs + price
    const product = window.ShopifyAnalytics?.meta?.product;
    if (!product) return;

    const selectedOptions = {};
    document.querySelectorAll('[data-option-btn].is-selected').forEach(b => {
      selectedOptions[b.dataset.optionName] = b.dataset.optionValue;
    });

    const variant = product.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[product.options[i]])
    );
    if (!variant) return;

    document.querySelectorAll('[data-variant-id], [data-variant-id-atc]').forEach(inp => {
      inp.value = variant.id;
    });

    const priceEl = document.querySelector('[data-price]');
    if (priceEl) priceEl.querySelector('.pdp-price__main').textContent = formatMoney(variant.price);

    const atcBtn = document.querySelector('[data-atc-btn]');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available
        ? `ADD TO CART · ${formatMoney(variant.price)}`
        : 'SOLD OUT';
    }
  });
});

/* ─── Utilities ─────────────────────────────────────────────────── */
function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
